import type { Dispatch, SetStateAction } from 'react';
import type { NavigateFunction, Params, SetURLSearchParams } from 'react-router-dom';
import type { DateRange, GlobalFilter, PageType } from '@/types';
import {
  RouteBuilder,
  getContentPerformanceQueryParams,
  getCurrentRouteParams,
} from '@/utils/routeUtils';
import { DateString } from '@/types/state/filter';
import { isDateString } from './dateUtils/dateValidationUtils';

/**
 * The filter/network state a page cares about, independent of how it is encoded in the URL.
 * `networkIds` holds every active network (a single id in path mode, the full list in query mode).
 */
export interface FilterRouteState {
  networkIds: string[];
  /** Selected show ids; an empty array means "all shows" (matching `GlobalFilter.selectedShows`). */
  showIds: string[];
  from?: DateString;
  to?: DateString;
}

interface ReadStateArgs {
  params: Readonly<Params<string>>;
  searchParams: URLSearchParams;
}

interface NavContext {
  pageType: PageType;
  navigate: NavigateFunction;
  setSearchParams: SetURLSearchParams;
  params: Readonly<Params<string>>;
  searchParams: URLSearchParams;
  /** Used when the URL has no dates yet, so path segments are never `undefined`. */
  defaultDateRange: DateRange;
  /** Writes show/date into FilterContext in the same turn as navigate so the next render is not mixed. */
  setFilters: Dispatch<SetStateAction<GlobalFilter>>;
}

/**
 * Encapsulates one URL "shape" for filter/network state so callers never branch on the URL mode.
 *
 * - {@link pathRouteStrategy} is the legacy `/:networkId/:page/:showId/:from/:to` shape.
 * - {@link queryRouteStrategy} is the `/content-performance?networks=&shows=&from=&to=` shape.
 *
 * The active strategy is chosen once (see `useFilterNavigation`); every consumer then talks to this
 * interface instead of re-deriving the mode and hand-building URLs.
 */
export interface FilterRouteStrategy {
  /** Reads the current filter/network state from the URL (path params or query string). */
  readState(args: ReadStateArgs): FilterRouteState;
  /**
   * Reads current URL state, merges `patch` onto it (omitted keys stay; `showIds: []` is an
   * explicit "all"), fills missing dates from {@link NavContext.defaultDateRange}, writes
   * show/date into FilterContext, and navigates.
   */
  applyFilterChange(
    patch: Partial<FilterRouteState>,
    context: NavContext,
    options?: { replace?: boolean }
  ): void;
}

const getMergedFilterState = (
  readState: FilterRouteStrategy['readState'],
  patch: Partial<FilterRouteState>,
  context: NavContext
): FilterRouteState => {
  const merged = {
    ...readState({ params: context.params, searchParams: context.searchParams }),
    ...patch,
  };
  return {
    ...merged,
    from: merged.from ?? context.defaultDateRange.from,
    to: merged.to ?? context.defaultDateRange.to,
  };
};

/**
 * Dual-write: FilterContext and URL so the next render has
 * matching show/date and network. Browser back/forward still goes through `useSyncUrlToFilters`.
 */
const writeFiltersAndNavigate = (
  nextState: FilterRouteState,
  context: NavContext,
  url: string,
  options?: { replace?: boolean }
): void => {
  context.setFilters((prev) => ({
    ...prev,
    selectedShows: nextState.showIds,
    dateRange: { from: nextState.from ?? context.defaultDateRange.from, to: nextState.to ?? context.defaultDateRange.to },
  }));
  context.navigate(url, { replace: options?.replace });
};

export const pathRouteStrategy: FilterRouteStrategy = {
  readState({ params }) {
    const { networkId, showId, from, to } = getCurrentRouteParams(params);
    const showIds = showId && showId !== 'all' ? [showId] : [];
    return { networkIds: networkId ? [networkId] : [], showIds, from: isDateString(from) ? from : undefined, to: isDateString(to) ? to : undefined };
  },

  applyFilterChange(patch, context, options) {
    const nextState = getMergedFilterState(pathRouteStrategy.readState, patch, context);
    const networkId = nextState.networkIds[0] ?? '';
    const showId = nextState.showIds[0] ?? 'all';

    // Mirrors the legacy buildFilterRoute: payouts has no show/date segment, everything else
    // uses the `/:networkId/:page/:showId/:from/:to` shape.
    const url =
      context.pageType === 'payouts'
        ? `/${networkId}/payouts`
        : `/${networkId}/${context.pageType}/${showId}/${nextState.from}/${nextState.to}`;

    writeFiltersAndNavigate(nextState, context, url, options);
  },
};

export const queryRouteStrategy: FilterRouteStrategy = {
  readState({ searchParams }) {
    return getContentPerformanceQueryParams(searchParams);
  },

  applyFilterChange(patch, context, options) {
    const nextState = getMergedFilterState(queryRouteStrategy.readState, patch, context);
    writeFiltersAndNavigate(
      nextState,
      context,
      RouteBuilder.contentPerformance({
        networkIds: nextState.networkIds,
        showIds: nextState.showIds,
        from: nextState.from,
        to: nextState.to,
      }),
      options
    );
  },
};
